import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { trackPageview } from '../analytics'

/**
 * Real-path routing on window.location.pathname, not hash routes.
 * The site is one page. Same-page hash targets keep native scrolling.
 */

const RouteContext = createContext<string>('/')
const SPA_PATHS = new Set(['/'])

function normalize(pathname: string): string {
  if (pathname !== '/' && pathname.endsWith('/')) return pathname.slice(0, -1)
  return pathname
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState<string>(() =>
    typeof window === 'undefined' ? '/' : normalize(window.location.pathname),
  )

  useEffect(() => {
    const onPop = () => {
      const nextPath = normalize(window.location.pathname)
      setPath(nextPath)
      trackPageview(nextPath)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href') || ''
      if (!href.startsWith('/') || href.startsWith('//')) return
      if (anchor.target && anchor.target !== '_self') return
      const url = new URL(href, window.location.origin)
      if (!SPA_PATHS.has(normalize(url.pathname))) return
      // Same-page hash targets keep the browser's own scroll.
      if (url.pathname === window.location.pathname && url.hash) return
      e.preventDefault()
      window.history.pushState({}, '', href)
      const nextPath = normalize(url.pathname)
      setPath(nextPath)
      trackPageview(nextPath)
      if (url.hash) {
        requestAnimationFrame(() => {
          document.getElementById(url.hash.slice(1))?.scrollIntoView({ behavior: 'auto', block: 'start' })
        })
      } else {
        window.scrollTo(0, 0)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  return <RouteContext.Provider value={path}>{children}</RouteContext.Provider>
}

export function usePathname(): string {
  return useContext(RouteContext)
}

/** Plain anchor with a real href. Crawlable with or without JavaScript. */
export function Link({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  )
}
